import { createFileRoute } from "@tanstack/react-router";
import { ogImageMeta } from "@/lib/seo/og-image";
import { LocaleLink } from "@/components/site/LocaleLink";
import { ArrowRight, Compass } from "lucide-react";
import { FaqSearch } from "@/components/site/FaqSearch";
import { Button } from "@/components/ui/button";
import { SubPagePillNav, MEDIA_ANALYSIS_PILL_ITEMS } from "@/components/site/SubPagePillNav";
import { MediaAnalysisHero } from "@/components/site/MediaAnalysisHero";
import { Reveal } from "@/components/site/Reveal";
import { mediaAnalysisFaqs } from "@/content/faqs";

import { pageSchemas } from "@/lib/seo/page-schema";
const META_DESCRIPTION =
  "Common questions about Perceptica's media analysis and media analyses - methodology, deliverables, timelines, languages and pricing. Read the answers.";

export const Route = createFileRoute("/media-analysis/faq")({
  head: () => ({
    meta: [
      { title: "Media Analysis FAQ - Media Analyses | Perceptica" },
      { name: "description", content: META_DESCRIPTION },
      { property: "og:title", content: "Media Analysis FAQ - Media Analyses | Perceptica" },
      { property: "og:description", content: META_DESCRIPTION },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Media Analysis FAQ - Media Analyses | Perceptica" },
      { name: "twitter:description", content: META_DESCRIPTION },
      { property: "og:url", content: "https://perceptica.com/media-analysis/faq" },
      ...ogImageMeta("en", "/media-analysis/faq"),
    ],
  
    scripts: pageSchemas({ locale: "en", path: "/media-analysis/faq", faq: mediaAnalysisFaqs }),
  }),
  component: MediaAnalysisFaqPage,
});

function MediaAnalysisFaqPage() {
  return (
    <div>
      <MediaAnalysisHero
        eyebrow="Media Analysis FAQ"
        headlineLead="Frequently Asked Questions about "
        headlineHighlight="Media Analysis"
        description="How our analysis works, who delivers it, what you get, and how it differs from the Media Listening Platform."
        ctas={[
          { label: "Talk to us", to: "/request-a-demo" },
          { label: "Browse questions", href: "#faqs" },
        ]}
      />
      <SubPagePillNav items={MEDIA_ANALYSIS_PILL_ITEMS} />

      {/* FAQS - LIGHT */}
      <section id="faqs" className="scroll-mt-32 bg-background py-24 lg:py-32">
        <div className="mx-auto max-w-[1100px] px-6 lg:px-10">
          <Reveal>
            <div className="flex flex-col items-start gap-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
                Questions & Answers
              </p>
              <h2 className="font-display max-w-3xl text-[clamp(1.85rem,3vw,2.5rem)] leading-[1.1] text-foreground">
                Everything you need to know about Media Analysis
              </h2>
              <p className="mt-2 max-w-2xl text-[15px] leading-relaxed text-foreground/70">
                Search the most common questions, or reach out directly if you want to talk to a senior analyst.
              </p>
            </div>
          </Reveal>

          <div className="mt-12">
            <FaqSearch items={mediaAnalysisFaqs} idPrefix="ma-faq" />
          </div>
        </div>
      </section>

      {/* CLOSING CTA - contained dark card on a LIGHT band */}
      <section className="relative bg-background pt-20 pb-28 lg:pt-24 lg:pb-32">
        <div className="mx-auto max-w-[1280px] px-6 lg:px-10">
          <Reveal>
            <div className="relative overflow-hidden rounded-3xl bg-primary text-primary-foreground shadow-[0_30px_80px_-30px_rgba(15,12,40,0.5)]">
              <div className="grid-overlay" />
              <div className="noise-overlay" />
              <div
                aria-hidden
                className="pointer-events-none absolute -top-24 -left-24 h-[360px] w-[360px] rounded-full opacity-40 blur-[120px]"
                style={{ background: "radial-gradient(circle, #6B5BF5 0%, transparent 70%)" }}
              />
              <div
                aria-hidden
                className="pointer-events-none absolute -bottom-32 -right-24 h-[380px] w-[380px] rounded-full opacity-40 blur-[120px]"
                style={{ background: "radial-gradient(circle, #FF2D78 0%, transparent 70%)" }}
              />
              <div className="relative grid gap-12 px-8 py-16 lg:grid-cols-[1.4fr_1fr] lg:items-center lg:gap-16 lg:px-16 lg:py-20">
                <div>
                  <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.04] px-3.5 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/80">
                    <Compass className="h-3.5 w-3.5 text-accent" />
                    Talk to a senior analyst
                  </span>
                  <h2 className="font-display mt-6 max-w-[22ch] text-[clamp(1.85rem,3.2vw,2.75rem)] leading-[1.05] text-white">
                    Still have questions about Media Analysis?
                  </h2>
                  <p className="mt-6 max-w-xl text-[15.5px] leading-relaxed text-white/70">
                    Tell us about your objectives and we will recommend the right analysis programme, one-off or ongoing.
                  </p>
                </div>
                <div className="flex flex-col gap-4 lg:items-end">
                  <Button asChild size="xl" className="w-full lg:w-auto">
                    <LocaleLink to="/request-a-demo">
                      Let's talk
                      <ArrowRight className="h-4 w-4" />
                    </LocaleLink>
                  </Button>
                  <p className="text-[12px] uppercase tracking-[0.18em] text-white/80">
                    Most requests receive a reply from us within one business day.
                  </p>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
